/**
 * EmojiPickerField — a button that shows the current emoji and opens a popover
 * grid organised by category. Also accepts a freeform custom-emoji input.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PRIMARY_BG } from '../../../theme/brand';
import {
  Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow,
  Box, Text, Grid, VStack, Input, Divider, HStack, useDisclosure,
} from '@chakra-ui/react';

const BRAND = PRIMARY_BG;

const CATEGORIES = [
  {
    label: 'Charts & Data',
    emojis: [
      '📊','📈','📉','📋','📌','📍','🎯','🔍','🔎','💡',
      '🗂️','📁','📂','📐','📏','🔢','🗃️','🧮','🗺️','📜',
      '📑','📄','📃','🖥️','💾','💿','📀','🖨️','⌨️','🖱️',
    ],
  },
  {
    label: 'Business & Finance',
    emojis: [
      '💼','🏢','🏬','🏦','🏧','💰','💵','💴','💶','💷',
      '💸','💳','🪙','💹','📊','🤑','🏷️','🛒','📦','🚢',
      '🚀','✈️','🏭','⚙️','🔧','🔨','🪛','🔩','🔑','🗝️',
      '📡','🌐','🔌','💡','🔋','📲','📱','☎️','📞','🖨️',
    ],
  },
  {
    label: 'People & Teams',
    emojis: [
      '👥','👤','🧑','👩','👨','🧑‍💼','👩‍💼','👨‍💼',
      '👨‍💻','👩‍💻','🧑‍💻','🧑‍🔬','👩‍🔬','👨‍🔬',
      '🏆','🥇','🥈','🥉','🤝','🫱','🫲','🙌','👏','💪',
      '🧠','🎓','👑','🎤','🎧','🫂','👋','🤜','🤛',
    ],
  },
  {
    label: 'Status & Alerts',
    emojis: [
      '✅','❎','❌','⚠️','🚨','🛑','⛔','🚫','💢','❗',
      '❕','❓','❔','🔴','🟠','🟡','🟢','🔵','🟣','⚫',
      '⚪','🟤','🔺','🔻','🔸','🔹','🔷','🔶','🔔','🔕',
      '📢','📣','🆕','🆙','🆒','🆓','🔝','💯','✨','🌟',
      '⭐','🌠','🎆','🎇','🔥','💥','🎉','🎊','🏅','🎖️',
    ],
  },
  {
    label: 'Time & Planning',
    emojis: [
      '🗓️','📅','📆','🕐','🕑','🕒','🕓','⏰','⏱️','⏲️',
      '⏳','⌛','🔄','🔃','🔀','🔁','🔂','▶️','⏸️','⏹️',
      '📌','📍','🗒️','✏️','📝','🖊️','🖋️','🖌️','📎','🗂️',
    ],
  },
  {
    label: 'Communication',
    emojis: [
      '💬','💭','🗨️','🗯️','📩','📨','📧','📥','📤','📬',
      '📭','📮','✉️','📝','📰','🗞️','📖','📚','📓','📔',
      '📒','📕','📗','📘','📙','🔊','🔇','📻','📺','🎙️',
      '🎚️','🎛️','📟','📠','🌍','🌎','🌏','🛜','📶','🔗',
    ],
  },
  {
    label: 'Health & Growth',
    emojis: [
      '💚','💙','❤️','🧡','💛','💜','🖤','🤍','🤎','❤️‍🔥',
      '🌱','🌿','🍀','🌳','🌲','🌾','🌻','🌺','🌸','🌹',
      '🍎','🍊','🍋','🫐','🍇','🍓','🏋️','🤸','🏃','🧘',
      '💊','🩺','🩹','🧬','🔬','🧪','🧫','💉','🩻','🫀',
    ],
  },
  {
    label: 'Tech & Science',
    emojis: [
      '🤖','🦾','🧬','🔬','🔭','🛰️','🚀','🛸','⚗️','🧪',
      '🧫','🧲','💡','🔋','🔌','💻','🖥️','📱','⌨️','🖱️',
      '🖨️','📡','☢️','⚛️','🌡️','🧯','🔭','🪐','🌌','🌠',
      '⚡','☄️','🌪️','🌊','🏔️','🗻','🌋','🏝️','🌅','🌄',
    ],
  },
  {
    label: 'Fun & Creative',
    emojis: [
      '🎨','🎭','🎬','🎮','🕹️','🎲','🧩','♟️','🎯','🎳',
      '🎸','🎹','🥁','🎺','🎻','🎵','🎶','🎤','🎧','🎼',
      '🏆','🥁','🎪','🎠','🎡','🎢','🎟️','🎫','🎰','🎴',
      '🃏','🀄','🎲','🧸','🪆','🎎','🎑','🎐','🎋','🎍',
    ],
  },
  {
    label: 'Animals & Nature',
    emojis: [
      '🦁','🐯','🐻','🦊','🐺','🦝','🐮','🐷','🐸','🐧',
      '🦅','🦆','🦉','🦚','🦜','🐬','🦈','🐙','🦋','🐝',
      '🦀','🦞','🐉','🦕','🦖','🦄','🐲','🌈','🌊','🏔️',
      '🌋','🏜️','🌵','🌴','🌿','🍄','🐚','🪸','🌊','⛰️',
    ],
  },
  {
    label: 'Food & Drink',
    emojis: [
      '☕','🍵','🧃','🥤','🍺','🍻','🥂','🍾','🧋','🍹',
      '🍕','🍔','🌮','🍜','🍣','🍱','🥗','🍰','🎂','🍩',
      '🍪','🍫','🍬','🍭','🍯','🥐','🍞','🧀','🥚','🥞',
    ],
  },
  {
    label: 'Travel & Places',
    emojis: [
      '🏠','🏡','🏘️','🏗️','🏛️','🏟️','🗼','🗽','🗿','🏰',
      '🏯','⛩️','🕌','🕍','🕋','🛕','⛪','🚂','🚃','🚄',
      '🚅','🚆','🚇','✈️','🚀','🛸','🚁','⛵','🚢','🛳️',
      '🚗','🚕','🚙','🛻','🚌','🏎️','🛺','🚲','🛵','🏍️',
    ],
  },
  {
    label: 'Symbols & Objects',
    emojis: [
      '💎','💍','👓','🕶️','🥽','🧳','☂️','☔','⚡','❄️',
      '🌈','🔥','💧','🌊','🌀','🌫️','🪄','🔮','🧿','🪬',
      '♻️','⚜️','🔱','🏵️','✡️','🔯','☯️','☮️','✝️','☸️',
      '🔐','🔒','🔓','🔏','🗝️','🔑','🏹','⚔️','🛡️','🪃',
    ],
  },
];

export function EmojiPickerField({ value, onChange }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [custom, setCustom] = useState('');

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustom(val);
    if (val.trim()) onChange(val.trim());
  };

  const handleEmojiClick = (em) => {
    onChange(em);
    setCustom('');
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" isLazy closeOnBlur>
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          w="48px"
          h="40px"
          borderRadius="8px"
          border="1.5px solid"
          borderColor="gray.200"
          bg="white"
          fontSize="22px"
          lineHeight={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ borderColor: BRAND, bg: 'var(--brand-tint)' }}
          _focus={{ outline: 'none', borderColor: BRAND, boxShadow: `0 0 0 3px ${BRAND}33` }}
          transition="all 0.15s"
          cursor="pointer"
          title="Pick emoji"
          onClick={onOpen}
        >
          {value || '📊'}
        </Box>
      </PopoverTrigger>

      <PopoverContent
        w="300px"
        maxH="420px"
        overflowY="auto"
        boxShadow="0 8px 32px rgba(0,0,0,0.14)"
        borderRadius="14px"
        border="1px solid #e2e8f0"
        _focus={{ outline: 'none' }}
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' },
        }}
      >
        <PopoverArrow />
        <PopoverBody p={3}>
          <VStack align="stretch" spacing={3}>
            {/* Custom / freeform input */}
            <Box>
              <Text fontSize="10px" fontWeight={700} color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                Custom emoji
              </Text>
              <HStack spacing={2}>
                <Box
                  w="36px" h="36px" borderRadius="8px" bg="gray.50"
                  border="1.5px solid" borderColor="gray.200"
                  display="flex" alignItems="center" justifyContent="center"
                  fontSize="20px" flexShrink={0}
                >
                  {value || '📊'}
                </Box>
                <Input
                  size="sm"
                  placeholder="Paste or type any emoji"
                  value={custom}
                  onChange={handleCustomChange}
                  borderRadius="7px"
                  fontSize="14px"
                  _focus={{ borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                />
              </HStack>
            </Box>

            <Divider />

            {/* Category grids */}
            {CATEGORIES.map((cat) => (
              <Box key={cat.label}>
                <Text
                  fontSize="10px" fontWeight={700} color="gray.400"
                  textTransform="uppercase" letterSpacing="wider" mb={1.5}
                >
                  {cat.label}
                </Text>
                <Grid templateColumns="repeat(8, 1fr)" gap="2px">
                  {cat.emojis.map((em) => (
                    <Box
                      key={em}
                      as="button"
                      type="button"
                      fontSize="17px"
                      w="32px"
                      h="32px"
                      borderRadius="7px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg={value === em ? 'var(--brand-tint)' : 'transparent'}
                      border="1.5px solid"
                      borderColor={value === em ? BRAND : 'transparent'}
                      _hover={{ bg: 'var(--brand-tint)', borderColor: BRAND, transform: 'scale(1.15)' }}
                      transition="all 0.1s"
                      cursor="pointer"
                      onClick={() => handleEmojiClick(em)}
                    >
                      {em}
                    </Box>
                  ))}
                </Grid>
              </Box>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

EmojiPickerField.propTypes = {
  value:    PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
